import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Crown, Medal, Plus, Trash2, Pencil, Save, Search, Loader2, Check, X, Star,
} from "lucide-react";
import { FeaturedBadge } from "@/components/dashboard/ProfileCard";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}
// ISO string -> value for <input type="date"> (YYYY-MM-DD)
function isoToDateInput(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}
// <input type="date"> value -> ISO string (start of day UTC), or "" to clear
function dateInputToIso(v, endOfDay = false) {
  if (!v) return "";
  return endOfDay ? `${v}T23:59:59.000Z` : `${v}T00:00:00.000Z`;
}

const EMPTY_FORM = {
  tier: "gold", starts_at: "", ends_at: "",
  provider_name: "", provider_contact: "", provider_value: "", provider_notes: "",
};

// Search + pick a profile to feature (used only in "add" mode).
function ProfilePicker({ value, onPick }) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/admin/profiles", { params: { search, page: 1, page_size: 8 } })
        .then(({ data }) => setItems(data.items || []))
        .catch((e) => { console.debug("profile search failed:", e); setItems([]); })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  if (value) {
    return (
      <div className="flex items-center gap-3 bg-[#09090b] border border-[#3f3f46] p-2" data-testid="featured-picked">
        <img src={imgProxy(value.photo_url, 160)} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} alt="" className="w-12 h-12 object-cover" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#fafafa] truncate">{value.name || "—"}</div>
          <div className="text-xs text-[#71717a]">{value.city || "—"} · {value.source_name || value.source}</div>
        </div>
        <button onClick={() => onPick(null)} className="text-[#71717a] hover:text-[#ef4444]" title="Trocar perfil"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative mb-2">
        <Search className="w-4 h-4 text-[#71717a] absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} autoFocus data-testid="featured-search"
          placeholder="Pesquisar perfil por nome, número ou cidade…"
          className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none pl-9 pr-3 py-2 text-sm" />
      </div>
      <div className="max-h-56 overflow-y-auto divide-y divide-[#27272a] border border-[#27272a]">
        {loading && <div className="px-3 py-4 text-center text-xs text-[#71717a]">A pesquisar…</div>}
        {!loading && items.length === 0 && <div className="px-3 py-4 text-center text-xs text-[#71717a]">Sem resultados.</div>}
        {items.map((p, i) => (
          <button key={p.id} type="button" onClick={() => onPick(p)} data-testid={`featured-pick-${i}`}
            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#27272a]/40">
            <img src={imgProxy(p.photo_url, 160)} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} alt="" className="w-10 h-10 object-cover shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#fafafa] truncate">{p.name || "—"}</div>
              <div className="text-xs text-[#71717a] truncate">{p.city || "—"} · {p.source_name || p.source} {p.phone ? `· ${p.phone}` : ""}</div>
            </div>
            {p.featured_tier && <FeaturedBadge tier={p.featured_tier} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function FeaturedDialog({ open, mode, record, onClose, onSaved }) {
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // Sync incoming record into local state on open (adjust-state-during-render pattern).
  const [prevKey, setPrevKey] = useState(null);
  const curKey = open ? (record?.id || "new") : null;
  if (curKey !== prevKey) {
    setPrevKey(curKey);
    if (open && mode === "edit" && record) {
      setPicked(record.profile);
      setForm({
        tier: record.tier || "gold",
        starts_at: isoToDateInput(record.starts_at),
        ends_at: isoToDateInput(record.ends_at),
        provider_name: record.provider_name || "",
        provider_contact: record.provider_contact || "",
        provider_value: record.provider_value || "",
        provider_notes: record.provider_notes || "",
      });
    } else if (open) {
      setPicked(null);
      setForm(EMPTY_FORM);
    }
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (mode === "add" && !picked) { toast.error("Escolhe um perfil primeiro"); return; }
    setSaving(true);
    try {
      const payload = {
        tier: form.tier,
        starts_at: dateInputToIso(form.starts_at, false),
        ends_at: dateInputToIso(form.ends_at, true),
        provider_name: form.provider_name,
        provider_contact: form.provider_contact,
        provider_value: form.provider_value,
        provider_notes: form.provider_notes,
      };
      if (mode === "add") {
        await api.post("/admin/featured", { profile_id: picked.id, ...payload });
        toast.success("Destaque criado");
      } else {
        await api.patch(`/admin/featured/${record.id}`, payload);
        toast.success("Destaque atualizado");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#18181b] border border-[#3f3f46] rounded-none text-[#fafafa] max-w-lg" data-testid="featured-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-normal text-lg">
            {mode === "add" ? "Novo destaque" : "Editar destaque"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          {mode === "add" && (
            <div>
              <label className="label-tiny block mb-1">Perfil</label>
              <ProfilePicker value={picked} onPick={setPicked} />
            </div>
          )}
          {mode === "edit" && picked && (
            <div className="flex items-center gap-3 bg-[#09090b] border border-[#3f3f46] p-2">
              <img src={imgProxy(picked.photo_url, 160)} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} alt="" className="w-12 h-12 object-cover" />
              <div className="min-w-0"><div className="text-sm text-[#fafafa] truncate">{picked.name || "—"}</div><div className="text-xs text-[#71717a]">{picked.city} · {picked.source_name}</div></div>
            </div>
          )}

          <div>
            <label className="label-tiny block mb-1">Nível</label>
            <div className="flex gap-2">
              {[["gold", "Ouro", Crown], ["silver", "Prata", Medal]].map(([t, label, Icon]) => (
                <button key={t} type="button" onClick={() => set("tier", t)} data-testid={`featured-tier-${t}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm border ${form.tier === t ? (t === "gold" ? "border-[#c8aa6e] bg-[#c8aa6e]/10 text-[#c8aa6e]" : "border-[#cbd5e1] bg-[#cbd5e1]/10 text-[#cbd5e1]") : "border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa]"}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-tiny block mb-1">Início (opcional)</label>
              <input type="date" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} data-testid="featured-starts"
                className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]" />
            </div>
            <div>
              <label className="label-tiny block mb-1">Fim (opcional)</label>
              <input type="date" value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} data-testid="featured-ends"
                className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]" />
            </div>
          </div>
          <p className="text-[11px] text-[#71717a] -mt-2">Datas vazias = destaque sempre ativo.</p>

          <div className="border-t border-[#27272a] pt-3">
            <div className="label-tiny mb-2">Dados de contrato (privado — só admin)</div>
            <div className="grid grid-cols-2 gap-3">
              {[["provider_name", "Anunciante"], ["provider_contact", "Contacto"], ["provider_value", "Valor pago"]].map(([k, label]) => (
                <div key={k} className={k === "provider_value" ? "col-span-2" : ""}>
                  <label className="label-tiny block mb-1">{label}</label>
                  <input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`featured-${k}`}
                    className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]" />
                </div>
              ))}
              <div className="col-span-2">
                <label className="label-tiny block mb-1">Notas</label>
                <textarea rows={2} value={form.provider_notes} onChange={(e) => set("provider_notes", e.target.value)} data-testid="featured-provider_notes"
                  className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e] resize-none" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa]">Cancelar</button>
            <button onClick={save} disabled={saving} data-testid="featured-save"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#c8aa6e] text-black hover:bg-[#d4b982] disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FeaturedView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode, record}

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/featured")
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => { console.debug("featured list failed:", e); setItems([]); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (rec) => {
    if (!window.confirm(`Remover o destaque de "${rec.profile?.name || "este perfil"}"?`)) return;
    try { await api.delete(`/admin/featured/${rec.id}`); toast.success("Destaque removido"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao remover"); }
  };

  const goldCount = items.filter((r) => r.tier === "gold").length;
  const silverCount = items.filter((r) => r.tier === "silver").length;
  const activeCount = items.filter((r) => r.active).length;

  return (
    <div className="space-y-4" data-testid="admin-featured">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm text-[#a1a1aa]">
          <span className="inline-flex items-center gap-1"><Crown className="w-4 h-4 text-[#c8aa6e]" /> {goldCount} Ouro</span>
          <span className="inline-flex items-center gap-1"><Medal className="w-4 h-4 text-[#cbd5e1]" /> {silverCount} Prata</span>
          <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-[#10b981]" /> {activeCount} ativos</span>
        </div>
        <button onClick={() => setDialog({ mode: "add" })} data-testid="featured-add"
          className="inline-flex items-center gap-1.5 bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-4 py-2.5 text-sm font-medium">
          <Plus className="w-4 h-4" /> Novo destaque
        </button>
      </div>

      <div className="bg-[#18181b] border border-[#27272a] overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead><tr className="label-tiny">
            <th className="px-4 py-3">Perfil</th><th className="px-4 py-3">Nível</th>
            <th className="px-4 py-3">Período</th><th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3">Anunciante</th><th className="px-4 py-3">Valor</th>
            <th className="px-4 py-3">Ações</th>
          </tr></thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={r.id} data-testid={`featured-row-${i}`} className="border-t border-[#27272a] hover:bg-[#27272a]/30">
                <td className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <img src={imgProxy(r.profile?.photo_url, 160)} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} alt="" className="w-10 h-10 object-cover shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[#fafafa] truncate">{r.profile?.name || "(perfil removido)"}</div>
                      <div className="text-xs text-[#71717a]">{r.profile?.city || "—"} · {r.profile?.source_name || ""}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2"><FeaturedBadge tier={r.tier} /></td>
                <td className="px-4 py-2 text-[#a1a1aa] whitespace-nowrap text-xs">{fmtDate(r.starts_at)} → {fmtDate(r.ends_at)}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 border ${r.active ? "border-[#10b981] text-[#10b981]" : "border-[#3f3f46] text-[#71717a]"}`}>
                    {r.active ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="px-4 py-2 text-[#a1a1aa]">{r.provider_name || "—"}</td>
                <td className="px-4 py-2 text-[#c8aa6e] font-mono">{r.provider_value || "—"}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setDialog({ mode: "edit", record: r })} data-testid={`featured-edit-${i}`} className="text-[#a1a1aa] hover:text-[#c8aa6e]" title="Editar"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(r)} data-testid={`featured-delete-${i}`} className="text-[#71717a] hover:text-[#ef4444]" title="Remover"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[#71717a] text-sm">
                <Star className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Ainda não há destaques. Cria o primeiro para o mostrar no topo da Visão Geral e dos Perfis.
              </td></tr>
            )}
            {loading && <tr><td colSpan={7} className="px-4 py-12 text-center text-[#71717a] text-sm">A carregar…</td></tr>}
          </tbody>
        </table>
      </div>

      <FeaturedDialog
        open={!!dialog}
        mode={dialog?.mode || "add"}
        record={dialog?.record}
        onClose={() => setDialog(null)}
        onSaved={() => { setDialog(null); load(); }}
      />
    </div>
  );
}
